import React, { useMemo, useState } from "react";
import { Filter, RotateCw, Flag, Edit3, MoreVertical, Clock, Calendar, ChevronDown, Check, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { formatTime, todayStr, taskBodyPreview, taskOccursOnIso, getOccurrencesOnIso, isOccurrenceSnoozed, daysDiff, actionableOccurrence, nextScheduledIso } from "@/utils";
import { SafeLinkText } from "@/components/SafeLinkText";
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ordinal = (n) => {
    if (n % 10 === 1 && n % 100 !== 11) return `${n}st`;
    if (n % 10 === 2 && n % 100 !== 12) return `${n}nd`;
    if (n % 10 === 3 && n % 100 !== 13) return `${n}rd`;
    return `${n}th`;
};

/** Buckets a recurring task's interval into one of the top-level categories the filter chips
 *  toggle on. Returns "custom" for anything that doesn't map cleanly (e.g. every 5 days). */
export const recurrenceCategory = (task) => {
    if (task.type !== "recurring") return null;
    const i = task.interval;
    if (i === 1) return "daily";
    if (i === 7 || i === 14) return "weekly";
    if (i === "month_end" || i === 30) return "monthly";
    if (i === 365) return "yearly";
    return "custom";
};

/** Human-readable description of the recurrence pattern (e.g. "14th of every month"). */
const patternText = (task) => {
    const i = task.interval;
    // Guard: a recurring task without a valid interval is malformed (usually a legacy AI
    // create where the model couldn't map "every 6th–11th of the month" to our fixed
    // {1,7,14,30,365} enum). Show something honest instead of "Every null days" and hint at
    // the fix so the user knows to edit the task.
    if (i === null || i === undefined || Number.isNaN(i)) return "No cadence · edit to set";
    let anchor = null;
    if (task.date) {
        const [y, m, d] = task.date.split("-").map(Number);
        anchor = new Date(y, m - 1, d);
    }
    if (i === 1) return "Every day";
    if (i === 7)  return anchor ? `Every ${WEEKDAYS[anchor.getDay()]}` : "Every week";
    if (i === 14) return anchor ? `Every other ${WEEKDAYS[anchor.getDay()]}` : "Every 2 weeks";
    if (i === "month_end") return "Last day of every month";
    if (i === 30) return anchor ? `${ordinal(anchor.getDate())} of every month` : "Every month";
    if (i === 31) return "Every 31 days";
    if (i === 365) return anchor ? `Every ${MONTHS[anchor.getMonth()]} ${ordinal(anchor.getDate())}` : "Every year";
    return `Every ${i} days`;
};

/** What a tap on this row may act on. Overdue-first, then today, never a future beat: the old
 *  forward-only walk (which chose the NEXT unchecked beat and wrote Home Depot's Oct 14 on Sep 16)
 *  is gone. `nextIso` is DISPLAY ONLY — the actionable day if one exists, else the next scheduled day. */
const rowModel = (task) => {
    const act = actionableOccurrence(task);
    const nextIso = act.displayIso || nextScheduledIso(task);
    return { act, nextIso };
};

const nextLabel = (iso) => {
    if (!iso) return "—";
    const d = daysDiff(iso);
    const [y, mo, dy] = iso.split("-").map(Number);
    const dt = new Date(y, mo - 1, dy);
    const short = `${MONTHS[mo - 1]} ${dy}`;
    if (d === 0) return "Today";
    if (d === 1) return `Tomorrow · ${short}`;
    return short;
};

const CATEGORY_STYLE = {
    daily:   { pill: "bg-emerald-500/15 border-emerald-400/50 text-emerald-300", label: "Daily" },
    weekly:  { pill: "bg-cyan-500/15   border-cyan-400/50   text-cyan-300",   label: "Weekly" },
    monthly: { pill: "bg-fuchsia-500/15 border-fuchsia-400/50 text-fuchsia-300", label: "Monthly" },
    yearly:  { pill: "bg-amber-500/15  border-amber-400/50  text-amber-300",  label: "Yearly" },
    custom:  { pill: "bg-zinc-500/15   border-zinc-400/50   text-zinc-300",   label: "Custom" },
};

const FILTER_CHIPS = [
    { id: "all",     label: "All"     },
    { id: "daily",   label: "Daily"   },
    { id: "weekly",  label: "Weekly"  },
    { id: "monthly", label: "Monthly" },
    { id: "yearly",  label: "Yearly"  },
    { id: "due",     label: "Due soon" },
];

/** Recurring section — Rituals & routines. A more dashboard-y layout: header w/ stats, dedicated
 *  search + filter, table-style rows with clear TASK · RECURRENCE · NEXT columns and inline
 *  actions. Reuses toggleTask / setEditingTask handlers from App.js. */
export const RecurringView = ({ tasks, onToggle, onEdit, onPickMissed, searchTerm = "", onTasksRepaired }) => {
    const [filter, setFilter] = useState("all");
    const [repairing, setRepairing] = useState(false);
    // Search is driven by the global app search bar (see App.js — same behavior as WeeklyView,
    // MonthlyView, and every other section). Removing the local `q` state means one search bar,
    // and typing in the header immediately filters recurring tasks like every other tab.
    const q = searchTerm || "";

    // Count rows that were saved by the old AI with `type=recurring` but no interval — those
    // rows show "No cadence · edit to set" and won't reliably route to Today/Overdue because
    // there's no schedule to compute from. Surface a repair banner when any exist.
    const brokenCount = useMemo(
        () => (tasks || []).filter((t) => t.type === "recurring" && !t.archived && !t.interval).length,
        [tasks],
    );

    const runRepair = async () => {
        if (repairing) return;
        setRepairing(true);
        try {
            const res = await api.repairNullRecurring();
            toast.success(`Healed ${res.fixed} row${res.fixed === 1 ? "" : "s"} — they'll surface as scheduled/todo now`);
            onTasksRepaired?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Repair failed");
        } finally {
            setRepairing(false);
        }
    };

    const rows = useMemo(() => {
        const today = todayStr();
        const nextWeek = (() => {
            const d = new Date(); d.setDate(d.getDate() + 7);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        })();
        return (tasks || [])
            .filter((t) => t.type === "recurring" && !t.archived)
            .map((t) => {
                const { act, nextIso } = rowModel(t);
                return { task: t, category: recurrenceCategory(t), nextIso, act };
            })
            .filter(({ task, category, nextIso }) => {
                if (q && !task.text.toLowerCase().includes(q.toLowerCase())) return false;
                if (filter === "all") return true;
                if (filter === "due") return nextIso && nextIso <= nextWeek;
                return category === filter;
            })
            .sort((a, b) => (a.nextIso || "").localeCompare(b.nextIso || ""));
    }, [tasks, q, filter]);

    const activeCount = useMemo(
        () => (tasks || []).filter((t) => t.type === "recurring" && !t.archived).length,
        [tasks],
    );
    const dueTodayCount = useMemo(() => {
        const today = todayStr();
        return (tasks || [])
            .filter((t) => t.type === "recurring" && !t.archived)
            .filter((t) => taskOccursOnIso(t, today) && !isOccurrenceSnoozed(t, today))
            .length;
    }, [tasks]);

    return (
        <div className="fade-up" data-testid="recurring-view">
            {/* Header — title left, stats right */}
            <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl border border-meye-fuchsia/40 bg-meye-fuchsia/10 flex items-center justify-center flex-shrink-0">
                        <RotateCw size={18} className="text-meye-fuchsia" strokeWidth={2} />
                    </div>
                    <div>
                        <h2 className="font-brand text-2xl sm:text-3xl font-extrabold uppercase tracking-tight bg-gradient-to-r from-meye-fuchsia to-violet-400 bg-clip-text text-transparent leading-none">
                            Recurring
                        </h2>
                        <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mt-1">rituals &amp; routines</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <StatCard value={activeCount} label="Active" tone="fuchsia" />
                    <StatCard value={dueTodayCount} label="Due today" tone={dueTodayCount > 0 ? "amber" : "zinc"} />
                </div>
            </div>

            {/* Repair banner — the old AI used to save `type=recurring, interval=null` when it
                couldn't map a day-range onto our {1,7,14,30,365} enum. Those rows read as
                "No cadence · edit to set" and won't route to Today/Overdue. One tap heals them
                all — converts each into a plain scheduled/todo so they surface correctly. */}
            {brokenCount > 0 && (
                <div className="mb-3 flex items-center gap-3 rounded-lg border border-amber-400/50 bg-amber-500/10 px-3 py-2.5" data-testid="recurring-repair-banner">
                    <Wrench size={16} className="text-amber-300 flex-shrink-0" strokeWidth={2} />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-brand font-bold uppercase tracking-widest text-amber-200">
                            {brokenCount} broken row{brokenCount === 1 ? "" : "s"}
                        </p>
                        <p className="text-[11px] text-amber-100/70 font-mono truncate">
                            Missing cadence — heal to surface in Today/Overdue.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={runRepair}
                        disabled={repairing}
                        className="text-[10px] font-brand font-bold uppercase tracking-widest px-3 py-1.5 rounded border border-amber-400 bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-wait flex-shrink-0"
                        data-testid="recurring-repair-btn"
                    >
                        {repairing ? "healing…" : "heal all"}
                    </button>
                </div>
            )}

            {/* Filter bar — search is driven by the global app search bar in the header. */}
            <div className="flex justify-end mb-3">
                <FilterButton current={filter} onPick={setFilter} />
            </div>

            {/* Filter chips row — expands when Filter button toggles it below */}
            <div className="flex flex-wrap gap-1.5 mb-3">
                {FILTER_CHIPS.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setFilter(c.id)}
                        className={`text-[10px] font-brand font-bold uppercase tracking-widest px-3 py-1 rounded-full border transition-colors ${
                            filter === c.id
                                ? "border-meye-fuchsia bg-meye-fuchsia/25 text-meye-fuchsia shadow-[0_0_10px_rgba(217,70,239,0.35)]"
                                : "border-meye-border bg-black/40 text-zinc-400 hover:text-white hover:border-zinc-500"
                        }`}
                        data-testid={`recurring-filter-${c.id}`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="rounded-xl border border-meye-border bg-meye-card/40 backdrop-blur-sm overflow-hidden">
                {/* Column header */}
                <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_180px_140px_auto] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-meye-border/70 bg-black/20">
                    <span>Task</span>
                    <span>Recurrence</span>
                    <span>Next</span>
                    <span />
                </div>

                {rows.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-zinc-500 font-mono">
                        {q ? "no matches — try another word" : "no recurring tasks yet"}
                    </div>
                ) : (
                    <div>
                        {rows.map(({ task, category, nextIso, act }) => (
                            <RecurringRow
                                key={task.id}
                                task={task}
                                category={category}
                                nextIso={nextIso}
                                act={act}
                                onToggle={onToggle}
                                onPickMissed={onPickMissed}
                                onEdit={onEdit}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const StatCard = ({ value, label, tone = "fuchsia" }) => {
    const cls = tone === "amber"
        ? "border-amber-400/40 bg-amber-500/10 text-amber-200"
        : tone === "zinc"
            ? "border-zinc-600/50 bg-zinc-800/30 text-zinc-400"
            : "border-meye-fuchsia/40 bg-meye-fuchsia/10 text-meye-fuchsia";
    return (
        <div className={`px-3 py-1.5 rounded-lg border ${cls} min-w-[74px] text-center`} data-testid={`recurring-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-2xl font-brand font-bold leading-none">{value}</div>
            <div className="text-[9px] uppercase tracking-widest font-mono mt-1 opacity-80">{label}</div>
        </div>
    );
};

const FilterButton = ({ current, onPick }) => (
    <button
        onClick={() => onPick(current === "all" ? "daily" : "all")}
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-full border border-meye-border bg-black/40 text-zinc-400 hover:text-white hover:border-zinc-500 font-brand font-bold text-[11px] uppercase tracking-widest flex-shrink-0"
        title="Cycle filters"
        data-testid="recurring-filter-btn"
    >
        <Filter size={13} strokeWidth={2} />
        Filter
    </button>
);

const RecurringRow = ({ task, category, nextIso, act, onToggle, onPickMissed, onEdit }) => {
    const style = CATEGORY_STYLE[category] || CATEGORY_STYLE.custom;
    // Edit opens on the actionable SOURCE key when one exists; otherwise on the next scheduled day
    // (editing/rescheduling a future beat is legitimate — completing it is not, and the server refuses).
    const editKey = act.key || nextIso;
    const openEdit = () => onEdit?.(editKey ? { ...task, _occKey: editKey, _displayIso: nextIso, time: (getOccurrencesOnIso(task, nextIso).find((o) => o.key === editKey)?.time) || task.time } : task);
    const checkIn = (e) => {
        e.stopPropagation();
        if (act.kind === "pick") { onPickMissed?.({ ...task, _missedOccurrences: act.missed, _missedCount: act.missed.length }); return; }
        if (act.key) { onToggle?.(task, act.key); return; }
        toast.message(`${task.text.slice(0, 40)} — nothing due yet${nextIso ? ` · next ${nextLabel(nextIso)}` : ""}`);
    };
    return (
        <div
            className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_180px_140px_auto] gap-3 items-center px-4 py-3 border-b border-meye-border/40 last:border-b-0 hover:bg-meye-cyan/[0.04] transition-colors cursor-pointer group"
            onClick={openEdit}
            data-testid={`recurring-row-${task.id}`}
            data-actionable={act.kind}
            data-actionable-key={act.key || undefined}
        >
            {/* TASK column: checkbox + priority + time + text */}
            <div className="flex items-center gap-2 min-w-0">
                <button
                    onClick={checkIn}
                    className="w-5 h-5 rounded border border-meye-border bg-black/40 hover:border-meye-cyan flex items-center justify-center flex-shrink-0"
                    data-testid={`recurring-checkin-${task.id}`}
                    title={act.kind === "pick" ? `${act.missed.length} missed — choose which` : act.kind === "overdue" ? `Check in the missed ${act.key} dose` : act.kind === "today" ? "Check in for today" : "Nothing due yet"}
                >
                    <Check size={12} className="text-transparent group-hover:text-meye-cyan/60" strokeWidth={2.5} />
                </button>
                {/* Priority flag — red=high, cyan=med (default for recurring), fuchsia=low.
                    Null/undefined reads as medium so all legacy recurring tasks show cyan. */}
                {(() => {
                    const p = task.priority || "med";
                    const cls = p === "high" ? "text-red-400" : p === "low" ? "text-meye-fuchsia" : "text-meye-cyan";
                    return <Flag size={14} className={`${cls} flex-shrink-0`} strokeWidth={2} aria-label={`${p} priority`} />;
                })()}
                {task.time && (
                    <span className="text-[10px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-violet-500/40 bg-violet-500/10 text-violet-300 flex items-center gap-1 flex-shrink-0">
                        <Clock size={9} strokeWidth={2} />
                        {formatTime(task.time)}
                    </span>
                )}
                <span className="min-w-0 flex flex-col">
                    <span className={`text-sm text-zinc-100 truncate ${taskBodyPreview(task) ? "font-semibold" : ""}`}><SafeLinkText text={task.text} /></span>
                    {taskBodyPreview(task) && <span className="text-[11px] text-zinc-500 line-clamp-1" data-testid={`recurring-body-${task.id}`}>{taskBodyPreview(task)}</span>}
                </span>
            </div>

            {/* RECURRENCE column: badge + pattern text */}
            <div className="flex items-center gap-2 min-w-0 col-span-2 sm:col-span-1">
                <span className={`text-[10px] font-brand font-bold uppercase tracking-widest px-2 py-0.5 rounded border flex-shrink-0 ${style.pill}`}>
                    {style.label}
                </span>
                <span className="text-[11px] text-zinc-400 font-mono truncate hidden sm:inline">{patternText(task)}</span>
            </div>

            {/* NEXT column */}
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-300 font-mono whitespace-nowrap">
                <Calendar size={12} className="text-meye-cyan/70 flex-shrink-0" strokeWidth={2} />
                <span>Next: <span className="text-meye-cyan">{nextLabel(nextIso)}</span></span>
            </div>

            {/* ACTIONS column */}
            <div className="flex items-center gap-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                <button
                    onClick={(e) => { e.stopPropagation(); onEdit?.(task); }}
                    className="p-1.5 rounded hover:bg-meye-cyan/10 text-zinc-400 hover:text-meye-cyan"
                    data-testid={`recurring-edit-${task.id}`}
                    title="Edit"
                >
                    <Edit3 size={13} strokeWidth={2} />
                </button>
                <button
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 rounded hover:bg-meye-cyan/10 text-zinc-500 hover:text-white"
                    data-testid={`recurring-more-${task.id}`}
                    title="More"
                >
                    <MoreVertical size={13} strokeWidth={2} />
                </button>
            </div>
        </div>
    );
};

export default RecurringView;
